import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { IShift, ITableItem, ITableView } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { FullTimePresentationPipe } from 'src/app/modules/core/pipes/full-time-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { IOperationalDiscipline } from 'src/app/modules/widget/interfaces/operational-discipline';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetBasicComponent } from '../../dashboard-widget-basic/dashboard-widget-basic.component';

@Component({
  selector: 'app-operational-discipline',
  templateUrl: 'operational-discipline.component.html',
  styleUrls: ['./operational-discipline.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OperationalDisciplineComponent extends DashboardWidgetBasicComponent implements OnInit, OnDestroy {
  public tableData: ITableView;
  public displayMessage: boolean;
  public subtitleWidget: string;

  constructor(
    public booleanBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    public cacheService: CacheService,
    public machineContext: MachineContextService,
    private langService: LanguageService,
    private fullTimePipe: FullTimePresentationPipe,
    private datePipe: DatePresentationPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(booleanBarService, communication, parameterService, cacheService, machineContext, screenViewService);
    this.displayMessage = false;
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  // eslint-disable-next-line
  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * build view to reload when date is changed
   *
   * @param values default value
   */
  public buildView(values: any): void {
    super.buildView(values);
    this.getData(values);
  }

  /**
   * Get data from API
   */
  private getData(operationalDisciplineData: IOperationalDiscipline[]): void {
    this.displayMessage = !operationalDisciplineData.length;
    this.getTableView(operationalDisciplineData);
  }

  /**
   * Get table data
   *
   * @param data data from api
   */
  private getTableView(data: IOperationalDiscipline[]): void {
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shiftName = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    this.subtitleWidget = `${this.langService.message('company')}: ${user.company.name},
    ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
    ${this.langService.message('date')}: ${this.datePipe.transform(date)},
    ${this.langService.message('shift_')}: ${shiftName.name}`;

    const rows = [];

    for (const operation of data) {
      const row = {
        data: operation as any,
        items: this.getItems(operation),
      };

      rows.push(row);
    }

    this.tableData = {
      headers: ['shift_', 'group', '1st_train', 'num_trains', 'cycle_closure', 'minute_delta'],
      dataSet: rows,
    };
  }

  /**
   * Get the <td> table item
   *
   * @param item Operational Discipline data
   */
  private getItems(item: IOperationalDiscipline): ITableItem[] {
    const tds: ITableItem[] = [];

    const tdShiftName: ITableItem = {
      text: item.shiftName ? item.shiftName : null,
    };
    const tdGroupName: ITableItem = {
      text: item.groupName ? item.groupName : null,
    };
    const tdFirstTrain: ITableItem = {
      badge: {
        text: item.firstTrainDatetime ? this.fullTimePipe.transform(item.firstTrainDatetime) : null,
        color: item.firstTrainRule === 1 || item.firstTrainRule === 0 ? this.getBadgeColor(item.firstTrainRule) : null,
        tooltip: `
        <p><b>${this.langService.message('1st_train').toUpperCase()}</b></p>
        <p>${item.firstTrainName ? item.firstTrainName : '-'}</p>
        <p>
          ${this.langService.message('estimated_time')}:
          <span>
            <b>${item.firstTrainHourLimit ? this.fullTimePipe.transform(item.firstTrainHourLimit) : '-'}</b>
          </span>
        </p>
        <p>${this.langService.message('check_in_time')}:
          <span class='${this.getSpanClass(item.firstTrainRule)}'>
            <b>${this.fullTimePipe.transform(item.firstTrainDatetime)}</b>
          </span>
        </p>
       `,
      },
    };
    const tdNumTrain: ITableItem = {
      badge: {
        text: item.trainsTandemQty ? item.trainsTandemQty : null,
        color: item.trainsTandemRule === 1 || item.trainsTandemRule === 0 ? this.getBadgeColor(item.trainsTandemRule) : null,
      },
    };
    const tdFirstCicle: ITableItem = {
      badge: {
        text: item.firstCicleDatetime ? this.fullTimePipe.transform(item.firstCicleDatetime) : null,
        color: item.firstCicleTrains === 1 || item.firstCicleTrains === 0 ? this.getBadgeColor(item.firstCicleTrains) : null,
        tooltip: `
        <p><b>${this.langService.message('cycle_closure').toUpperCase()}</b></p>
        <p>${item.lastTrainName ? item.lastTrainName : '-'}</p>
        <p>
          ${this.langService.message('estimated_time')}:
          <span>
            <b>${item.minTrainTandemHour ? this.fullTimePipe.transform(item.minTrainTandemHour) : '-'}</b>
          </span>
        </p>
        <p>${this.langService.message('check_in_time')}:
          <span class='${this.getSpanClass(item.firstCicleTrains)}'>
            <b>${this.fullTimePipe.transform(item.firstCicleDatetime)}</b>
          </span>
        </p>
       `,
      },
    };
    const tdGapMinutes: ITableItem = {
      badge: {
        text: item.gapMinutes ? item.gapMinutes : null,
        color: item.gapMinutesRule === 1 || item.gapMinutesRule === 0 ? this.getBadgeColor(item.gapMinutesRule) : null,
      },
    };

    tds.push(tdShiftName);
    tds.push(tdGroupName);
    tds.push(tdFirstTrain);
    tds.push(tdNumTrain);
    tds.push(tdFirstCicle);
    tds.push(tdGapMinutes);

    return tds;
  }

  /**
   * Get color to span text
   *
   * @param value 0 or 1
   */
  private getSpanClass(value: number): string {
    let spanClass = '';

    if (value === 1) {
      spanClass = 'mt-tooltip-success-text';
    } else if (value === 0) {
      spanClass = 'mt-tooltip-warning-text';
    }

    return spanClass;
  }

  /**
   * Get background color to badge
   *
   * @param value 0 or 1
   */
  private getBadgeColor(value: number): string {
    let color = '';

    if (value === 1) {
      return (color = '#22D983');
    }
    if (value === 0) {
      color = '#F05050';
    }

    return color;
  }

  /**
   * On destroy component
   */
  public onDestroy(): void {
    super.ngOnDestroy();
  }
}
